import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { GraduationCap, LogOut, LayoutDashboard, CalendarDays, BarChart3, Crown } from "lucide-react";

const OWNER_EMAIL = "newagelearning@thehaddisacademy.com";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isOwner = user?.email === OWNER_EMAIL;
  const onHomeschoolPage = location.pathname === "/homeschool";

  const onLogout = () => { logout(); nav("/"); };

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} data-testid="brand-logo" className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-500 flex items-center justify-center shadow-lg shadow-sky-200">
            <GraduationCap className="text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-black text-lg text-slate-900">The Haddis Academy LLC</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">K-5 · VA SOL Aligned</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {user && (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 flex items-center gap-2">
                <LayoutDashboard size={16} strokeWidth={2.5}/> Dashboard
              </Link>
              <Link to="/schedule" data-testid="nav-schedule" className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 flex items-center gap-2">
                <CalendarDays size={16} strokeWidth={2.5}/> Schedule
              </Link>
              <Link to="/progress" data-testid="nav-progress" className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100 flex items-center gap-2">
                <BarChart3 size={16} strokeWidth={2.5}/> Progress
              </Link>
              <Link to="/professional-development" data-testid="nav-pd" className="px-4 py-2 rounded-full text-sm font-bold text-violet-700 hover:bg-violet-50 flex items-center gap-2">
                <GraduationCap size={16} strokeWidth={2.5}/> PD Course
              </Link>
              {isOwner && (
                <Link to="/owner" data-testid="nav-owner" className="px-4 py-2 rounded-full text-sm font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 flex items-center gap-2">
                  <Crown size={16} strokeWidth={2.5}/> Owner stats
                </Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <div className="hidden sm:flex flex-col items-end leading-tight mr-2">
                <span className="text-sm font-bold text-slate-900">{user.student_name}</span>
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Grade {user.grade}</span>
              </div>
              <Button data-testid="logout-btn" variant="ghost" onClick={onLogout} className="rounded-full">
                <LogOut size={16} strokeWidth={2.5}/>
              </Button>
            </>
          ) : (
            <>
              {onHomeschoolPage ? (
                <Link to="/" data-testid="nav-pd-public" className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-bold text-violet-700 hover:bg-violet-50 items-center gap-2">
                  <GraduationCap size={16} strokeWidth={2.5}/> PD for Teachers
                </Link>
              ) : (
                <Link to="/homeschool" data-testid="nav-homeschool-public" className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-bold text-sky-700 hover:bg-sky-50 items-center gap-2">
                  <BarChart3 size={16} strokeWidth={2.5}/> K-5 Homeschool Curriculum
                </Link>
              )}
              <Link to="/auth?mode=login" data-testid="nav-login" className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100">Log in</Link>
              <Link to="/auth?mode=signup" data-testid="nav-signup">
                <Button className="rounded-full bg-sky-400 hover:bg-sky-500 text-white font-bold px-6 shadow-lg shadow-sky-200">
                  Get Started
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
